import React, { useCallback, useEffect, useState } from 'react';
import FeatherIcon from 'feather-icons-react';
import { Button } from '../components/Button';
import { ApiService, ValidationHealthPath, ValidationHealthResponse } from '../services/apiService';

function formatTimestamp(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-NZ', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatHours(hours: number | null): string {
  if (hours === null) return '—';
  if (hours < 1) return `${Math.round(hours * 60)} min ago`;
  if (hours < 48) return `${Math.round(hours)} hr ago`;
  return `${Math.round(hours / 24)} days ago`;
}

const PATH_LABELS: Record<keyof Pick<ValidationHealthResponse, 'email' | 'sr'>, string> = {
  email: 'Email referrals',
  sr: 'SR referrals',
};

const PATH_ICONS: Record<keyof Pick<ValidationHealthResponse, 'email' | 'sr'>, string> = {
  email: 'mail',
  sr: 'inbox',
};

const PathCard: React.FC<{ pathKey: 'email' | 'sr'; data: ValidationHealthPath | null; loading: boolean }> = ({
  pathKey,
  data,
  loading,
}) => {
  if (loading) {
    return (
      <div className="validation-health-card validation-health-card--loading">
        <FeatherIcon icon="loader" size={18} className="spin" />
        <span>Loading…</span>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="validation-health-card validation-health-card--error">
        <FeatherIcon icon="alert-triangle" size={18} />
        <span>No data</span>
      </div>
    );
  }

  const statusClass = data.healthy
    ? 'validation-health-card--healthy'
    : 'validation-health-card--unhealthy';
  const statusIcon = data.healthy ? 'check-circle' : 'alert-triangle';
  const statusLabel = data.healthy ? 'Working' : 'Not working';

  const statusEntries = Object.entries(data.by_status_7d).sort((a, b) => b[1] - a[1]);

  return (
    <div className={`validation-health-card ${statusClass}`}>
      <div className="validation-health-card-header">
        <FeatherIcon icon={PATH_ICONS[pathKey]} size={18} />
        <span className="validation-health-card-title">{PATH_LABELS[pathKey]}</span>
        <span className={`validation-health-light ${data.healthy ? 'green' : 'red'}`}>
          <FeatherIcon icon={statusIcon} size={16} />
          {statusLabel}
        </span>
      </div>
      <dl className="validation-health-details">
        <div>
          <dt>Last successful validation</dt>
          <dd>
            {formatTimestamp(data.last_success_at)}
            {data.hours_since_success !== null && (
              <span className="validation-health-ago"> ({formatHours(data.hours_since_success)})</span>
            )}
          </dd>
        </div>
        <div>
          <dt>Last validation attempt</dt>
          <dd>{formatTimestamp(data.last_validated_at)}</dd>
        </div>
        <div>
          <dt>Validations (7 days)</dt>
          <dd>{data.total_7d}</dd>
        </div>
      </dl>
      {statusEntries.length > 0 && (
        <div className="validation-health-statuses">
          {statusEntries.map(([status, count]) => (
            <span key={status} className="validation-health-chip">
              {status}: {count}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export const ValidationHealthPage: React.FC = () => {
  const [health, setHealth] = useState<ValidationHealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ApiService.getValidationHealth();
      setHealth(data);
      setLastRefreshed(new Date());
    } catch {
      setError('Unable to load validation health. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Refresh every 5 minutes while the page is open.
    const interval = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [load]);

  return (
    <div className="validation-health-page">
      <div className="validation-health-header">
        <div>
          <h1>ACC Validation Health</h1>
          <p className="validation-health-subtitle">
            Green means at least one successful ACC registry validation on that intake path in the
            last {health?.health_window_hours ?? 24} hours. Counts cover the last{' '}
            {health?.lookback_days ?? 7} days.
          </p>
        </div>
        <div className="validation-health-header-actions">
          {lastRefreshed && (
            <span className="validation-health-refreshed">
              Updated {lastRefreshed.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <Button variant="secondary" size="sm" onClick={load} disabled={loading}>
            <FeatherIcon icon="refresh-cw" size={14} />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <div className="validation-health-error" role="alert">
          <FeatherIcon icon="alert-circle" size={16} />
          {error}
        </div>
      )}

      <div className="validation-health-grid">
        <PathCard pathKey="email" data={health?.email ?? null} loading={loading} />
        <PathCard pathKey="sr" data={health?.sr ?? null} loading={loading} />
      </div>
    </div>
  );
};
